import React, { useEffect, useRef, useState } from 'react'
import LabelText from '../../../ui/LabelText'
import LabelNumber from '../../../styled-components/LabelNumber'
import { getBackgroundColor, ISimulationAttributesData } from '../../../types/IPerformanceVersion2'
import { ChemistryPlayerPositions } from '../../../types/IOption'
import AttributesBar from './AttributesBar'
import UpArrow from "../../../assets/icons/sort_arrow_asc.png"
import DownArrow from "../../../assets/icons/NegativeArrow.png"
import { hasPlayersFromDifferentClubs } from '../../../utils/drawChemistrys'

interface ISimulationAttributesProps{
    initialAttributesData : any
    simulationAttributesData : ISimulationAttributesData
    inPlayerIds : number[]
    outPlayerIds : number[]
    positionResults: ChemistryPlayerPositions[]
    teamId: number
}

const SimulationAttributes : React.FC<ISimulationAttributesProps> = ({initialAttributesData , simulationAttributesData , inPlayerIds , outPlayerIds, positionResults, teamId}) => {

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [subAttrWidth, setSubAttrWidth] = useState<number>(300);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateWidth = () => {
            const el = container.querySelector('.simulation-sub-attribute-container') as HTMLElement | null;
            setSubAttrWidth(el?.clientWidth || container.clientWidth || 300);
        };

        // Initial measurement
        updateWidth();

        // ResizeObserver scoped to the container for more granular updates
        let ro: ResizeObserver | null = null;
        if ((window as any).ResizeObserver) {
            ro = new ResizeObserver(() => updateWidth());
            try { ro.observe(container); } catch (e) { /* ignore */ }
        }

        // Fallback to window resize
        window.addEventListener('resize', updateWidth);

        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    const getDifferenceValue = (beforeValue : number , afterValue : number) => {
        return (afterValue - beforeValue).toFixed(2)
    }

    const shouldShowDelta = inPlayerIds.length! > 0 && outPlayerIds?.length! > 0;

    const DeltaDisplay: React.FC<{
        before: number;
        after: number;
        shouldShow: boolean;
    }> = ({ before, after, shouldShow }) => {
        const diff = Number(getDifferenceValue(before, after));

        if (!shouldShow || diff === 0 || !hasPlayersFromDifferentClubs({positionResults, teamId})) return null;

        return (
            <>
                <img height={14} width={10} src={diff > 0 ? UpArrow : DownArrow} alt="trend arrow"/>
                <LabelNumber className='event-player-card-details' style={{color: diff > 0 ? "#00D1A8" : "#FF3864"}}>
                    {Math.abs(diff)}
                </LabelNumber>
            </>
        );
    };

    return (
    <div className="simulation-attributes-container">
        <div className="simulation-attributes-content" ref={containerRef}>
            <div className="attribute-individual-section">
                <div className="pitch-selection-container">
                    <LabelText className="player-search-text">Defensive</LabelText>
                        <div className='form-buttons-container'>
                            <DeltaDisplay
                                before={initialAttributesData?.defence?.avgScore!}
                                after={simulationAttributesData?.teamAttributesNew?.defence?.avgScore!}
                                shouldShow={shouldShowDelta}
                            />
                            <div className="values-content"  style={{backgroundColor: getBackgroundColor(initialAttributesData?.defence?.avgScore!)}}>
                                <LabelNumber className='event-player-card-details'>{initialAttributesData?.defence?.avgScore!}</LabelNumber>
                            </div>
                        </div>
                </div>
                <div className='simulation-sub-attribute-container'>
                    <div className="form-checkbox-list">
                        <div className='form-buttons-container'>
                            <LabelText className="notification-text">Tackling</LabelText>
                            <DeltaDisplay
                                before={initialAttributesData?.defence?.tackling?.score!}
                                after={simulationAttributesData?.teamAttributesNew?.defence?.tackling?.score!}
                                shouldShow={shouldShowDelta}
                            />
                        </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.defence?.tackling?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.defence?.tackling?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.defence?.tackling?.score! : initialAttributesData?.defence?.tackling?.score!)}
                            ifStar={inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.defence?.tackling?.star! : initialAttributesData?.defence?.tackling?.star!}
                            positionResults={positionResults}
                            teamId={teamId}
                        />
                    </div>
                    <div className="form-checkbox-list">
                            <div className='form-buttons-container'>
                                <LabelText className="notification-text">Defensive headers</LabelText>
                            <DeltaDisplay
                                before={initialAttributesData?.defence?.defensiveHeaders?.score!}
                                after={simulationAttributesData?.teamAttributesNew?.defence?.defensiveHeaders?.score!}
                                shouldShow={shouldShowDelta}
                            />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.defence?.defensiveHeaders?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.defence?.defensiveHeaders?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.defence?.defensiveHeaders?.score! : initialAttributesData?.defence?.defensiveHeaders?.score!)}
                            ifStar={inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.defence?.defensiveHeaders?.star! : initialAttributesData?.defence?.defensiveHeaders?.star!}
                            positionResults={positionResults}
                            teamId={teamId}
                            />

                    </div>
                    <div className="form-checkbox-list">
                             <div className='form-buttons-container'>
                                <LabelText className="notification-text">Ball recovery</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.defence?.aggression?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.defence?.aggression?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.defence?.aggression?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.defence?.aggression?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.defence?.aggression?.score! : initialAttributesData?.defence?.aggression?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}
                            />

                    </div>
                </div>
            </div>
             <div className="attribute-individual-section">
                <div className="pitch-selection-container">
                    <LabelText className="player-search-text">Build up play</LabelText>
                    <div className='form-buttons-container'>
                            <DeltaDisplay
                                before={initialAttributesData?.buildUpPlay?.avgScore!}
                                after={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.avgScore!}
                                shouldShow={shouldShowDelta}
                            />
                            <div className="values-content"  style={{backgroundColor: getBackgroundColor(initialAttributesData?.buildUpPlay?.avgScore!)}}>
                                <LabelNumber className='event-player-card-details'>{initialAttributesData?.buildUpPlay?.avgScore!}</LabelNumber>
                            </div>
                    </div>
                </div>
                <div className='simulation-sub-attribute-container'>
                    <div className="form-checkbox-list">
                             <div className='form-buttons-container'>
                                <LabelText className="notification-text">Creativity</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.buildUpPlay?.creativity?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.creativity?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.buildUpPlay?.creativity?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.creativity?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.buildUpPlay?.creativity?.score! : initialAttributesData?.buildUpPlay?.creativity?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}/>

                    </div>
                    <div className="form-checkbox-list">
                             <div className='form-buttons-container'>
                                <LabelText className="notification-text">Passing accuracy</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.buildUpPlay?.passingAccuracy?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.passingAccuracy?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.buildUpPlay?.passingAccuracy?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.passingAccuracy?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.buildUpPlay?.passingAccuracy?.score! : initialAttributesData?.buildUpPlay?.passingAccuracy?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}/>

                    </div>
                    <div className="form-checkbox-list">
                            <div className='form-buttons-container'>
                                <LabelText className="notification-text">Dribbling</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.buildUpPlay?.dribbling?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.dribbling?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.buildUpPlay?.dribbling?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.dribbling?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.buildUpPlay?.dribbling?.score! : initialAttributesData?.buildUpPlay?.dribbling?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}/>

                    </div>
                    <div className="form-checkbox-list">
                            <div className='form-buttons-container'>
                                <LabelText className="notification-text">Crossing</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.buildUpPlay?.crossing?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.crossing?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.buildUpPlay?.crossing?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.buildUpPlay?.crossing?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.buildUpPlay?.crossing?.score! : initialAttributesData?.buildUpPlay?.crossing?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}/>

                    </div>
                </div>
            </div>
            <div className="attribute-individual-section">
                <div className="pitch-selection-container">
                    <LabelText className="player-search-text">Final third</LabelText>
                    <div className='form-buttons-container'>
                        <DeltaDisplay
                            before={initialAttributesData?.finalThird?.avgScore!}
                            after={simulationAttributesData?.teamAttributesNew?.finalThird?.avgScore!}
                            shouldShow={shouldShowDelta}
                        />
                        <div className="values-content"  style={{backgroundColor: getBackgroundColor(initialAttributesData?.finalThird?.avgScore!)}}>
                            <LabelNumber className='event-player-card-details'>{initialAttributesData?.finalThird?.avgScore!}</LabelNumber>
                        </div>
                    </div>
                </div>
                <div className='simulation-sub-attribute-container'>
                    <div className="form-checkbox-list">
                            <div className='form-buttons-container'>
                                <LabelText className="notification-text">Finishing - Shots</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.finalThird?.finishingShots?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.finalThird?.finishingShots?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.finalThird?.finishingShots?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.finalThird?.finishingShots?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.finalThird?.finishingShots?.score! : initialAttributesData?.finalThird?.finishingShots?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}/>

                    </div>
                    <div className="form-checkbox-list">
                            <div className='form-buttons-container'>
                                <LabelText className="notification-text">Finishing - Aerial</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.finalThird?.finishingAerial?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.finalThird?.finishingAerial?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.finalThird?.finishingAerial?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.finalThird?.finishingAerial?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.finalThird?.finishingAerial?.score! : initialAttributesData?.finalThird?.finishingAerial?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}/>

                    </div>
                    <div className="form-checkbox-list">
                            <div className='form-buttons-container'>
                                <LabelText className="notification-text">Attack positioning</LabelText>
                                <DeltaDisplay
                                    before={initialAttributesData?.finalThird?.attackPositioning?.score!}
                                    after={simulationAttributesData?.teamAttributesNew?.finalThird?.attackPositioning?.score!}
                                    shouldShow={shouldShowDelta}
                                />
                            </div>
                            <AttributesBar
                            height={28}
                            width={subAttrWidth}
                            beforeValue={initialAttributesData?.finalThird?.attackPositioning?.score!}
                            afterValue={simulationAttributesData?.teamAttributesNew?.finalThird?.attackPositioning?.score!}
                            inPlayerIds={inPlayerIds}
                            outPlayerIds={outPlayerIds}
                            color={getBackgroundColor(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 ? simulationAttributesData?.teamAttributesNew?.finalThird?.attackPositioning?.score! : initialAttributesData?.finalThird?.attackPositioning?.score!)}
                            positionResults={positionResults}
                            teamId={teamId}/>
                    </div>
                </div>
            </div>
        </div>
    </div>
  )
}

export default SimulationAttributes